import { Routes, Route } from 'react-router-dom';
import './App.scss';
import DealerAppFormPage from './Components/Pages/DealerAppFormPage/DealerAppFormPage';
import EmailPreferencesPage from './Components/Pages/EmailPreferencesPage/EmailPreferencesPage';
import PreQualifyFormPage from './Components/Pages/PreQualifyFormPage/PreQualifyFormPage';
import CustomerPortalPage from './Components/Pages/CustomerPortalPage/CustomerPortalPage';
import ApplicationRouter from './Components/Pages/ApplicationFormPage/ApplicationRouter';
import FinicityPortalPage from './Components/Pages/FinicityFormPage/FinicityPortalPage';
import DepositPage from './Components/Pages/DepositPage/DepositPage';
import { useSelector } from 'react-redux';
import { AppState } from './Store/rootReducer';
import { useAppDispatch } from './Store/useAppDispatch';
import { setLeadSourceTrackingData } from './Store/Shared/actions';
import { LeadSourceTrackingData } from './Models/LeadSourceParameters';
import CuratedVehiclesPage from "./Components/Pages/CuratedVehiclesPage/CuratedVehiclesPage";
import CuratedVehicleDetailsPage from "./Components/Pages/CuratedVehicleDetailsPage/CuratedVehicleDetailsPage";
import {useEffect, useRef, useState} from "react";
import {IframeTestPage} from "./Components/Pages/IframeTestPage/IframeTestPage";
import PortalDownPaymentThankYouPage from "./Components/Pages/PortalDownPaymentThankYouPage";
import DocumentViewerPage from "./Components/Pages/DocumentViewerPage/DocumentViewerPage";

function App() {
  const dispatch = useAppDispatch();
  const isInIframe = window.self !== window.top;
  const headerHtml = useSelector((appState: AppState) => appState.shared.headerHtml);
  const footerHtml = useSelector((appState: AppState) => appState.shared.footerHtml);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerAndFooterHeight, setHeaderAndFooterHeight] = useState<number>(0);
  // Add event listener to the message, which is sent into this component when hosted in an iFrame.
  // We will update Redux state with the lead source parameter data from the message.
  window.addEventListener("message", (event) => {
    const data: LeadSourceTrackingData = event.data;
    // Check if the data is an object and has the expected properties
    // If not, we will not update the Redux state.
    if (typeof data !== 'object' || !data || !data.utmParameters || !data.trackingData) {
      return;
    }
    // If the data is valid, we will update the Redux state with the lead source parameters
    dispatch(setLeadSourceTrackingData(data));
  });

  useEffect(() => {
    if (headerRef.current) {
      const height = isInIframe
        ? 0 // If we are in an iframe, we don't need to calculate the height
        : (headerRef.current?.offsetHeight ?? 0) + (footerRef.current?.offsetHeight ?? 0);
      setHeaderAndFooterHeight(height);
    }
  }, [headerHtml, footerHtml]);
  
  return (
    <>
      {!isInIframe && headerHtml && (
        <div style={{display: 'flex', width: '100%'}} dangerouslySetInnerHTML={{ __html: headerHtml }} ref={headerRef}></div>
      )}
      <Routes>
        {/* Special non-tenant-scoped route for the embedded document viewer (AMS 2000).
            Does NOT use the /:clientId convention — tenant context is derived from the viewer token.
            Must remain above /:clientId routes to avoid being matched as a clientId. */}
        <Route path='/document-viewer' element={<DocumentViewerPage />} />
        <Route path='/:clientId' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/credit-app' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/credit-app/salesperson/:salesPersonId' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/credit-app/:amsCode/salesperson/:salesPersonId' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/credit-app/:amsCode' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/dealerapp' element={<DealerAppFormPage />} />
        <Route path='/:clientId/dealerapp/:amsCode' element={<DealerAppFormPage />} />
        <Route path='/:clientId/deposit/:encodedUrl' element={<DepositPage headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/payment-thank-you/:customerPortalLinkId' element={<PortalDownPaymentThankYouPage headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/curated/:customerPortalLinkId' element={<CuratedVehiclesPage headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/curated/:customerPortalLinkId/:vehicleId' element={<CuratedVehicleDetailsPage headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/portal/credit-app/:customerPortalLinkId/:customerId' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/portal/new-credit-app/:customerPortalLinkId' element={<ApplicationRouter headerAndFooterHeight={headerAndFooterHeight} />} />
        <Route path='/:clientId/portal/:customerPortalLinkId' element={<CustomerPortalPage />} />
        <Route path='/:clientId/portal/:customerPortalLinkId/:step' element={<CustomerPortalPage />} />
        <Route path='/:clientId/prequalify' element={<PreQualifyFormPage />} />
        <Route path='/:clientId/prequalify/salesperson/:salesPersonId' element={<PreQualifyFormPage />} />
        <Route path='/:clientId/prequalify/:amsCode/salesperson/:salesPersonId' element={<PreQualifyFormPage />} />
        <Route path='/:clientId/prequalify/:amsCode' element={<PreQualifyFormPage />} />
        <Route path='/:clientId/portal/prequalify/:customerPortalLinkId' element={<PreQualifyFormPage />} />
        <Route path='/:clientId/portal/:customerPortalLinkId/incomeVerification' element={<FinicityPortalPage />} />
        <Route path='/:clientId/:emailAddress' element={<EmailPreferencesPage />} />
        <Route path='/:clientId/iframeTest' element={<IframeTestPage iframeSrc={'http://localhost:3000/RVD/credit-app?useCuratedSite=true'}/>} />
        <Route path='*' element={<div>Page Not Found</div>} />
      </Routes>
      {!isInIframe && footerHtml && (
        <div style={{display: 'flex', width: '100%'}} dangerouslySetInnerHTML={{ __html: footerHtml }} ref={footerRef}></div>
      )}
    </>
  )
}

export default App;
